import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Home as HomeIcon } from '@mui/icons-material';
import { Box, Paper } from '@mui/material';
import { SIDEBAR_CONFIG } from '@core/constants/config';
import { useAppLayout } from '@core/contexts/LayoutContext';
import { buildBreadcrumbs } from '@core/utils/breadcrumbs';
import { getHomePath, getResolvedRoute } from '@core/utils/routes';
import LayoutAppBar from './LayoutAppBar';
import LayoutBreadcrumbs from './LayoutBreadcrumbs';
import LayoutMainContent from './LayoutMainContent';
import Sidebar from './Sidebar';

const WebOneLayout: React.FC = () => {
  const {
    theme,
    sidebarOpen,
    sidebarWidth,
    toggleSidebar,
    showBreadcrumbs,
    visibleNavItems,
    routeConfig,
    userAccount,
    expandedLogo: ExpandedLogo,
  } = useAppLayout();
  const location = useLocation();

  const hasExpandedLogo = ExpandedLogo != null;
  const resolvedLogoHeight = hasExpandedLogo
    ? ExpandedLogo.logoHeight
    : SIDEBAR_CONFIG.LOGO_HEIGHT;
  const showSidebarLogo = !hasExpandedLogo || !sidebarOpen;

  const homePath = useMemo(
    () => getHomePath(visibleNavItems),
    [visibleNavItems]
  );

  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const resolved = getResolvedRoute(pathname, visibleNavItems);
    const items = buildBreadcrumbs(pathname, visibleNavItems, resolved);
    return items.map((item) => ({
      ...item,
      icon:
        item.path === homePath ? <HomeIcon sx={{ fontSize: 16 }} /> : undefined,
    }));
  }, [location.pathname, visibleNavItems, homePath]);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      {ExpandedLogo ? (
        <ExpandedLogo.component
          sidebarOpen={sidebarOpen}
          logoHeight={resolvedLogoHeight}
        />
      ) : null}

      <Sidebar
        open={sidebarOpen}
        onToggle={toggleSidebar}
        navItems={visibleNavItems}
        logoHeight={resolvedLogoHeight}
        showLogo={showSidebarLogo}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          maxHeight: '100vh',
          minWidth: 0,
          maxWidth: '100%',
          position: 'relative',
          zIndex: 1000,
          marginLeft: `${sidebarWidth}px`,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <LayoutAppBar
          logoHeight={resolvedLogoHeight}
          userAccount={userAccount}
          startAdornment={
            showBreadcrumbs ? <LayoutBreadcrumbs items={breadcrumbs} /> : null
          }
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '32px',
              p: 5,
              pt: 5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '--Paper-shadow': 'none',
            }}
          >
            <LayoutMainContent routeConfig={routeConfig} />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default WebOneLayout;
